import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { clientsAPI, customFieldsAPI, tasksAPI, documentsAPI, invoicesAPI, notesAPI, messagesAPI, pipelinesAPI, clientPipelinesAPI, teamAPI, tagsAPI, servicesAPI, clientPasswordsAPI, lookupValuesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import AddressForm from '../components/AddressForm';
import InvoiceForm from '../components/InvoiceForm';
import InvoiceViewDialog from '../components/InvoiceViewDialog';
import PaymentModeDialog from '../components/PaymentModeDialog';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import ConditionalField from '../components/ConditionalField';
import ValidatedInput from '../components/ValidatedInput';
import ClientSearchInput from '../components/ClientSearchInput';
import { ClientOverviewTab, ClientTasksTab, ClientNotesTab, ClientDocumentsTab, ClientInvoicesTab, ClientPasswordsTab, ClientWorkflowTab } from '../components/client-detail';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Calendar,
  Building2,
  User,
  CheckSquare,
  FileText,
  Clock,
  Tag,
  Loader2,
  DollarSign,
  Workflow,
  Plus,
  CheckCircle2,
  Circle,
  Pencil,
  X,
  Trash2,
  Send,
  MapPin,
  Download,
  Eye,
  ExternalLink,
  Check,
  MoreVertical,
  Shield,
  Key
} from 'lucide-react';
import { format } from 'date-fns';

// Helper function to format name to Title Case (First Letter Capital)
const formatToTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to format SIN number (XXX-XXX-XXX)
const formatSIN = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Helper function to format phone number ((XXX) XXX-XXXX)
const formatPhoneNumber = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Helper function to format email to lowercase
const formatEmail = (value) => {
  if (!value) return '';
  return value.toLowerCase();
};

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission, user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check permissions
  const canDeleteTask = hasPermission('canDeleteTask');
  const canDeleteNote = hasPermission('canDeleteNote');
  const canDeleteMessage = hasPermission('canDeleteMessage');
  const canDeleteInvoice = hasPermission('canDeleteInvoice');
  const canDeleteDocument = hasPermission('canDeleteDocument');
  const canDeleteClient = hasPermission('canDeleteClient');
  const canDeleteTag = user?.role === 'Admin' || user?.role === 'Manager';
  const [customFields, setCustomFields] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notes, setNotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [clientPipelineAssignments, setClientPipelineAssignments] = useState([]);
  const [linkedCorporations, setLinkedCorporations] = useState([]);
  const [ownerClient, setOwnerClient] = useState(null);
  
  // Edit Client Dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [individualClients, setIndividualClients] = useState([]);
  
  // Team members and tags for assignment
  const [teamMembers, setTeamMembers] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [services, setServices] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  
  // Add Task Dialog
  const [taskDialog, setTaskDialog] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '', description: '', assignedTo: '', dueDate: '', priority: 'medium', status: 'pending'
  });
  
  // Add Note Dialog
  const [noteDialog, setNoteDialog] = useState(false);
  const [newNote, setNewNote] = useState({ content: '', isPinned: false });
  
  // Add Invoice Dialog - uses InvoiceForm component
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  
  // Edit Invoice Dialog
  const [editInvoiceDialog, setEditInvoiceDialog] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  
  // Delete Invoice Dialog
  const [deleteInvoiceDialog, setDeleteInvoiceDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  
  // View Invoice Dialog
  const [viewInvoiceDialog, setViewInvoiceDialog] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  
  // Payment Mode Dialog
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [invoiceToPay, setInvoiceToPay] = useState(null);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  
  // Client Code Editing
  const [editingClientCode, setEditingClientCode] = useState(false);
  const [tempClientCode, setTempClientCode] = useState('');
  
  // Send Message Dialog
  const [messageDialog, setMessageDialog] = useState(false);
  const [newMessage, setNewMessage] = useState({ subject: '', body: '' });
  
  // Add Document Dialog
  const [documentDialog, setDocumentDialog] = useState(false);
  const [newDocument, setNewDocument] = useState({ name: '', type: 'Tax Return', file: null });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // View Document Dialog
  const [viewDocDialog, setViewDocDialog] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  
  // Tags Management
  const [tagsDialog, setTagsDialog] = useState(false);

  // Passwords Management
  const [clientPasswords, setClientPasswords] = useState([]);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [editPasswordDialog, setEditPasswordDialog] = useState(false);
  const [editingPassword, setEditingPassword] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [newPassword, setNewPassword] = useState({
    serviceName: '',
    username: '',
    password: '',
    website: '',
    notes: '',
    customFields: []
  });

  // Note mentions
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCursorPos, setMentionCursorPos] = useState(0);

  useEffect(() => {
    fetchClient();
    fetchCustomFields();
    fetchTasks();
    fetchDocuments();
    fetchInvoices();
    fetchNotes();
    fetchPipelines();
    fetchClientPipelines();
    fetchTeamMembers();
    fetchTags();
    fetchServices();
    fetchClientPasswords();
    fetchLookupValues();
  }, [id]);

  const fetchLookupValues = async () => {
    try {
      const response = await lookupValuesAPI.getAll();
      setIndustries(response.data.industries || []);
      setReferralSources(response.data.referralSources || []);
    } catch (error) {
      console.error('Error fetching lookup values:', error);
    }
  };

  const fetchClient = async () => {
    try {
      setLoading(true);
      const response = await clientsAPI.getById(id);
      setClient(response.data);
      
      // Fetch linked corporations for Individual clients
      if (response.data.type === 'Individual' && response.data.corporations?.length > 0) {
        const corpPromises = response.data.corporations.map(corpId => 
          clientsAPI.getById(corpId).catch(() => null)
        );
        const corpResults = await Promise.all(corpPromises);
        setLinkedCorporations(corpResults.filter(r => r?.data).map(r => r.data));
      } else {
        setLinkedCorporations([]);
      }
      
      // Fetch owner info for Corporation clients
      if (response.data.type === 'Corporation' && response.data.ownerId) {
        try {
          const ownerResponse = await clientsAPI.getById(response.data.ownerId);
          setOwnerClient(ownerResponse.data);
        } catch {
          setOwnerClient(null);
        }
      } else {
        setOwnerClient(null);
      }
    } catch (error) {
      console.error('Error fetching client:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getMembers();
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await tagsAPI.getAll();
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchClientPasswords = async () => {
    try {
      const response = await clientPasswordsAPI.getAll(id);
      setClientPasswords(response.data.passwords || []);
    } catch (error) {
      console.error('Error fetching client passwords:', error);
    }
  };

  const handleRevealPassword = async (passwordId) => {
    if (revealedPasswords[passwordId]) {
      // Hide password
      setRevealedPasswords(prev => {
        const newState = { ...prev };
        delete newState[passwordId];
        return newState;
      });
    } else {
      // Reveal password (fetch decrypted)
      try {
        const response = await clientPasswordsAPI.getDecrypted(passwordId);
        setRevealedPasswords(prev => ({
          ...prev,
          [passwordId]: response.data.password
        }));
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to reveal password', variant: 'destructive' });
      }
    }
  };

  const handleAddPassword = async () => {
    if (!newPassword.serviceName || !newPassword.password) {
      toast({ title: 'Error', description: 'Service name and password are required', variant: 'destructive' });
      return;
    }

    try {
      await clientPasswordsAPI.create({
        clientId: id,
        ...newPassword
      });
      toast({ title: 'Success', description: 'Password saved securely' });
      setPasswordDialog(false);
      setNewPassword({ serviceName: '', username: '', password: '', website: '', notes: '', customFields: [] });
      fetchClientPasswords();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save password', variant: 'destructive' });
    }
  };

  const openEditPasswordDialog = async (pwd) => {
    try {
      const response = await clientPasswordsAPI.getDecrypted(pwd._id);
      setEditingPassword(response.data);
      setEditPasswordDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load password', variant: 'destructive' });
    }
  };

  const handleUpdatePassword = async () => {
    if (!editingPassword) return;

    try {
      await clientPasswordsAPI.update(editingPassword._id, editingPassword);
      toast({ title: 'Success', description: 'Password updated' });
      setEditPasswordDialog(false);
      setEditingPassword(null);
      fetchClientPasswords();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update password', variant: 'destructive' });
    }
  };

  const handleDeletePassword = async (passwordId) => {
    if (!confirm('Are you sure you want to delete this password?')) return;

    try {
      await clientPasswordsAPI.delete(passwordId);
      toast({ title: 'Success', description: 'Password deleted' });
      fetchClientPasswords();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete password', variant: 'destructive' });
    }
  };

  const fetchPipelines = async () => {
    try {
      const response = await pipelinesAPI.getAll();
      setPipelines(response.data.pipelines || []);
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    }
  };

  const fetchClientPipelines = async () => {
    try {
      const response = await clientPipelinesAPI.getAll({ client_id: id });
      setClientPipelineAssignments(response.data.assignments || []);
    } catch (error) {
      console.error('Error fetching client pipeline assignments:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await customFieldsAPI.getAll();
      setCustomFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await tasksAPI.getAll({ client_id: id });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await documentsAPI.getAll({ client_id: id });
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await invoicesAPI.getAll({ client_id: id });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await notesAPI.getAll(id);
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await messagesAPI.getAll(id);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Staff Assignment
  const handleAssignStaff = async (staffName) => {
    try {
      await clientsAPI.update(id, { ...client, assignedTo: staffName });
      toast({ title: 'Success', description: `Client assigned to ${staffName}` });
      fetchClient();
    } catch (error) {
      console.error('Error assigning staff:', error);
      toast({ title: 'Error', description: 'Failed to assign staff', variant: 'destructive' });
    }
  };

  // Client Code Update
  const handleSaveClientCode = async () => {
    if (!tempClientCode.trim()) {
      toast({ title: 'Error', description: 'Client code cannot be empty', variant: 'destructive' });
      return;
    }
    try {
      await clientsAPI.update(id, { clientCode: tempClientCode.trim().toUpperCase() });
      toast({ title: 'Success', description: `Client code updated to ${tempClientCode.toUpperCase()}` });
      setEditingClientCode(false);
      fetchClient();
    } catch (error) {
      console.error('Error updating client code:', error);
      toast({ title: 'Error', description: 'Failed to update client code', variant: 'destructive' });
    }
  };

  // Tags Management
  const handleAddTag = async (tagName) => {
    const currentTags = client.tags || [];
    if (currentTags.includes(tagName)) return;
    
    try {
      await clientsAPI.update(id, { ...client, tags: [...currentTags, tagName] });
      toast({ title: 'Success', description: `Tag "${tagName}" added` });
      fetchClient();
    } catch (error) {
      console.error('Error adding tag:', error);
      toast({ title: 'Error', description: 'Failed to add tag', variant: 'destructive' });
    }
  };

  const handleRemoveTag = async (tagName) => {
    const currentTags = client.tags || [];
    try {
      await clientsAPI.update(id, { ...client, tags: currentTags.filter(t => t !== tagName) });
      toast({ title: 'Success', description: `Tag "${tagName}" removed` });
      fetchClient();
    } catch (error) {
      console.error('Error removing tag:', error);
      toast({ title: 'Error', description: 'Failed to remove tag', variant: 'destructive' });
    }
  };

  // Add Task
  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.assignedTo || !newTask.dueDate) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    try {
      await tasksAPI.create({
        ...newTask,
        clientId: id,
        client: client.name,
        dueDate: new Date(newTask.dueDate).toISOString(),
        tags: []
      });
      toast({ title: 'Success', description: 'Task created' });
      setTaskDialog(false);
      setNewTask({ title: '', description: '', assignedTo: '', dueDate: '', priority: 'medium', status: 'pending' });
      fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      toast({ title: 'Error', description: 'Failed to create task', variant: 'destructive' });
    }
  };

  // Add Note
  const handleCreateNote = async () => {
    if (!newNote.content) {
      toast({ title: 'Error', description: 'Please enter note content', variant: 'destructive' });
      return;
    }
    try {
      const authorName = user?.firstName && user?.lastName 
        ? `${user.firstName} ${user.lastName}` 
        : user?.name || 'Unknown';
      
      await notesAPI.create({
        clientId: id,
        content: newNote.content,
        isPinned: newNote.isPinned,
        authorId: user?.id,
        authorName: authorName
      });
      toast({ title: 'Success', description: 'Note added' });
      setNoteDialog(false);
      setNewNote({ content: '', isPinned: false });
      fetchNotes();
    } catch (error) {
      console.error('Error creating note:', error);
      toast({ title: 'Error', description: 'Failed to create note', variant: 'destructive' });
    }
  };

  // Handle @ mention in notes
  const handleNoteContentChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewNote(prev => ({ ...prev, content: value }));
    
    // Check for @ mention
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Show suggestions if @ is followed by letters (no space)
      if (!textAfterAt.includes(' ')) {
        setMentionFilter(textAfterAt.toLowerCase());
        setMentionCursorPos(cursorPos);
        setShowMentionSuggestions(true);
        return;
      }
    }
    setShowMentionSuggestions(false);
  };

  const handleSelectMention = (member) => {
    const textBeforeCursor = newNote.content.substring(0, mentionCursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = newNote.content.substring(mentionCursorPos);
    
    const memberName = member.firstName && member.lastName 
      ? `${member.firstName} ${member.lastName}` 
      : member.name;
    
    const newContent = textBeforeCursor.substring(0, lastAtIndex) + 
      `@${memberName} ` + 
      textAfterCursor;
    
    setNewNote(prev => ({ ...prev, content: newContent }));
    setShowMentionSuggestions(false);
  };

  const filteredMentionMembers = teamMembers.filter(member => {
    const name = (member.firstName && member.lastName 
      ? `${member.firstName} ${member.lastName}` 
      : member.name || '').toLowerCase();
    return name.includes(mentionFilter);
  });

  // Delete Note
  const handleDeleteNote = async (noteId) => {
    try {
      await notesAPI.delete(noteId);
      toast({ title: 'Success', description: 'Note deleted' });
      fetchNotes();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({ title: 'Error', description: 'Failed to delete note', variant: 'destructive' });
    }
  };

  // Delete Client (Admin only)
  const handleDeleteClient = async () => {
    if (!confirm(`Are you sure you want to delete "${client?.name}"? This will also delete all associated tasks, notes, documents, invoices, and pipeline assignments. This action cannot be undone.`)) {
      return;
    }
    
    try {
      await clientsAPI.delete(id);
      toast({ title: 'Success', description: `Client "${client?.name}" deleted successfully` });
      navigate('/clients');
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({ title: 'Error', description: 'Failed to delete client', variant: 'destructive' });
    }
  };

  // Add Invoice
  // Send Message
  const handleSendMessage = async () => {
    if (!newMessage.body) {
      toast({ title: 'Error', description: 'Please enter message content', variant: 'destructive' });
      return;
    }
    try {
      await messagesAPI.send({
        clientId: id,
        subject: newMessage.subject,
        body: newMessage.body,
        sender: 'John Anderson',
        senderType: 'staff'
      });
      toast({ title: 'Success', description: 'Message sent' });
      setMessageDialog(false);
      setNewMessage({ subject: '', body: '' });
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    }
  };

  // Upload Document
  const handleUploadDocument = async () => {
    if (!newDocument.name) {
      toast({ title: 'Error', description: 'Document name is required', variant: 'destructive' });
      return;
    }
    setUploadingDoc(true);
    try {
      // If file is selected, upload it first
      let fileUrl = null;
      let fileSize = 'N/A';
      
      if (newDocument.file) {
        const formData = new FormData();
        formData.append('file', newDocument.file);
        formData.append('clientId', id);
        
        const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          fileUrl = uploadData.fileUrl;
          fileSize = `${(newDocument.file.size / 1024).toFixed(1)} KB`;
        }
      }
      
      await documentsAPI.create({
        name: newDocument.name,
        clientId: id,
        clientName: client?.name || '',
        type: newDocument.type,
        status: 'pending_review',
        size: fileSize,
        uploadedBy: 'John Anderson',
        fileUrl: fileUrl
      });
      toast({ title: 'Success', description: 'Document uploaded' });
      setDocumentDialog(false);
      setNewDocument({ name: '', type: 'Tax Return', file: null });
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({ title: 'Error', description: 'Failed to upload document', variant: 'destructive' });
    } finally {
      setUploadingDoc(false);
    }
  };

  // View Document
  const handleViewDocument = (doc) => {
    setViewingDoc(doc);
    setViewDocDialog(true);
  };

  // Download Document
  const handleDownloadDocument = (doc) => {
    if (doc.fileUrl) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}${doc.fileUrl}`, '_blank');
    } else {
      toast({ title: 'Info', description: 'No file attached to this document' });
    }
  };

  // Delete Document
  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await documentsAPI.delete(docId);
      toast({ title: 'Success', description: 'Document deleted' });
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({ title: 'Error', description: 'Failed to delete document', variant: 'destructive' });
    }
  };

  // Get document status color
  const getDocStatusColor = (status) => {
    switch (status) {
      case 'signed':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending_review':
      case 'in_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending_signature':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Invoice Action Handlers
  const openPaymentDialog = (invoice) => {
    setInvoiceToPay(invoice);
    setSelectedPaymentMode('');
    setPaymentDialog(true);
  };

  const handleTogglePaid = async (invoice) => {
    if (invoice.status === 'paid') {
      // Unpay - no dialog needed
      try {
        await invoicesAPI.unpay(invoice._id);
        toast({ title: 'Success', description: 'Invoice marked as unpaid' });
        fetchInvoices();
      } catch (error) {
        console.error('Error updating invoice:', error);
        toast({ title: 'Error', description: 'Failed to update invoice', variant: 'destructive' });
      }
    } else {
      // Open payment mode dialog
      openPaymentDialog(invoice);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedPaymentMode) {
      toast({ title: 'Error', description: 'Please select a payment mode', variant: 'destructive' });
      return;
    }
    try {
      await invoicesAPI.pay(invoiceToPay._id, selectedPaymentMode);
      toast({ title: 'Success', description: 'Invoice marked as paid' });
      setPaymentDialog(false);
      setInvoiceToPay(null);
      setSelectedPaymentMode('');
      fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast({ title: 'Error', description: 'Failed to update invoice', variant: 'destructive' });
    }
  };

  const openEditInvoiceDialog = (invoice) => {
    setEditInvoice({
      ...invoice,
      hstRate: (invoice.hstRate || 0.13) * 100 // Convert to percentage
    });
    setEditInvoiceDialog(true);
  };

  const addEditInvoiceLineItem = () => {
    setEditInvoice(prev => ({
      ...prev,
      lineItems: [...(prev.lineItems || []), { service: '', year: '', description: '', amount: 0 }]
    }));
  };

  const updateEditInvoiceLineItem = (index, field, value) => {
    setEditInvoice(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) =>
        i === index ? { ...item, [field]: field === 'amount' ? parseFloat(value) || 0 : value } : item
      )
    }));
  };

  const removeEditInvoiceLineItem = (index) => {
    setEditInvoice(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const calculateEditInvoiceTotals = () => {
    if (!editInvoice) return { subtotal: 0, hstAmount: 0, grandTotal: 0 };
    const subtotal = (editInvoice.lineItems || []).reduce((sum, item) => sum + (item.amount || 0), 0);
    const hstAmount = subtotal * ((editInvoice.hstRate || 13) / 100);
    const grandTotal = subtotal + hstAmount;
    return { subtotal, hstAmount, grandTotal };
  };

  const handleUpdateInvoice = async () => {
    if (!editInvoice) return;
    try {
      const { subtotal, hstAmount, grandTotal } = calculateEditInvoiceTotals();
      await invoicesAPI.update(editInvoice._id, {
        dueDate: editInvoice.dueDate,
        lineItems: editInvoice.lineItems,
        hstRate: editInvoice.hstRate / 100,
        subtotal,
        hstAmount,
        grandTotal
      });
      toast({ title: 'Success', description: 'Invoice updated' });
      setEditInvoiceDialog(false);
      setEditInvoice(null);
      fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast({ title: 'Error', description: 'Failed to update invoice', variant: 'destructive' });
    }
  };

  const openDeleteInvoiceDialog = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteInvoiceDialog(true);
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    try {
      await invoicesAPI.delete(invoiceToDelete._id);
      toast({ title: 'Success', description: `Invoice ${invoiceToDelete.invoiceNumber} deleted` });
      setDeleteInvoiceDialog(false);
      setInvoiceToDelete(null);
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({ title: 'Error', description: 'Failed to delete invoice', variant: 'destructive' });
    }
  };

  // Edit Client Functions
  const openEditDialog = async () => {
    // Fetch individual clients for corporation owner linking
    try {
      const response = await clientsAPI.getAll({ type: 'Individual' });
      setIndividualClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching individual clients:', error);
    }
    
    // Copy client data to edit state, ensuring address is properly structured
    const defaultAddress = { line1: '', line2: '', city: '', province: '', country: '', postalCode: '' };
    setEditClient({
      ...client,
      address: client.address && typeof client.address === 'object' 
        ? { ...defaultAddress, ...client.address }
        : defaultAddress,
      spouse: client.spouse || { name: '', email: '', phone: '', sin: '', dateOfBirth: '' },
      children: client.children || [],
      dateOfBirth: client.dateOfBirth || '',
      maritalStatus: client.maritalStatus || '',
      customFields: client.customFields || {}
    });
    setEditDialog(true);
  };

  const addEditChild = () => {
    setEditClient(prev => ({
      ...prev,
      children: [...(prev.children || []), { name: '', dateOfBirth: '', sin: '' }]
    }));
  };

  const removeEditChild = (index) => {
    setEditClient(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const updateEditChild = (index, field, value) => {
    setEditClient(prev => ({
      ...prev,
      children: prev.children.map((child, i) => 
        i === index ? { ...child, [field]: value } : child
      )
    }));
  };

  const handleUpdateClient = async () => {
    if (!editClient.name || !editClient.email) {
      toast({ title: 'Error', description: 'Name and email are required', variant: 'destructive' });
      return;
    }
    
    try {
      await clientsAPI.update(id, editClient);
      toast({ title: 'Success', description: 'Client updated successfully' });
      setEditDialog(false);
      fetchClient(); // Refresh client data
    } catch (error) {
      console.error('Error updating client:', error);
      toast({ title: 'Error', description: 'Failed to update client', variant: 'destructive' });
    }
  };

  const updateEditSpouse = (field, value) => {
    setEditClient(prev => ({
      ...prev,
      spouse: { ...prev.spouse, [field]: value }
    }));
  };

  const updateEditCustomField = (fieldId, value) => {
    setEditClient(prev => ({
      ...prev,
      customFields: { ...prev.customFields, [fieldId]: value }
    }));
  };

  const getApplicableCustomFields = () => {
    const clientType = editClient?.type || client?.type;
    return customFields.filter(field => {
      // If appliesTo is empty or not set, show for all types
      if (!field.appliesTo || field.appliesTo.length === 0) return true;
      // Check if the client type is in the appliesTo list
      return field.appliesTo.includes(clientType);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Client not found</h2>
        <Button onClick={() => navigate('/clients')} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  const applicableCustomFields = customFields.filter(field => 
    field.appliesTo?.includes(client?.type) || !field.appliesTo
  );

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'in_progress': 'bg-blue-100 text-blue-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800',
      'scheduled': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getInvoiceStatusColor = (status) => {
    const colors = {
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'overdue': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/clients')}
        className="gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Clients
      </Button>

      {/* Client Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-2xl font-bold">
            {client.name.charAt(0)}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="client-detail-name">
              {client.type === 'Individual' && client.spouse?.name 
                ? `${client.name} & ${client.spouse.name}` 
                : client.name}
              {client.clientCode && (
                <span className="ml-2 text-blue-600 font-mono">({client.clientCode})</span>
              )}
            </h1>
            <p className="text-gray-500 mt-1">{client.contactPerson}</p>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <Badge className={`${
                client.status === 'active' ? 'bg-green-100 text-green-800' : 
                client.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                'bg-gray-100 text-gray-800'
              }`}>
                {client.status ? client.status.charAt(0).toUpperCase() + client.status.slice(1) : 'Active'}
              </Badge>
              <Badge variant="outline">{client.type}</Badge>
              {client.industry && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {client.industry}
                </Badge>
              )}
              {client.referralSource && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                  {client.referralSource}
                </Badge>
              )}
              {client.assignedTo && (
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                  Assigned: {client.assignedTo}
                </Badge>
              )}
              {client.tags && client.tags.map((tag, idx) => (
                <Badge key={idx} variant="outline" className="gap-1">
                  <Tag className="h-3 w-3" />
                  {tag}
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if(window.confirm(`Delete tag "${tag}"?`)) {
                        handleRemoveTag(tag);
                      }
                    }}
                    className="ml-1 hover:text-red-500 text-red-400"
                    title={`Click to remove "${tag}"`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {client.tags && client.tags.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    const badTags = client.tags.filter(t => 
                      ['hardev', 'other', ''].includes(t.toLowerCase().trim())
                    );
                    if (badTags.length > 0) {
                      if (window.confirm(`Remove these tags: ${badTags.join(', ')}?`)) {
                        for (const tag of badTags) {
                          await handleRemoveTag(tag);
                        }
                      }
                    } else {
                      toast({ title: 'Info', description: 'No bad tags found to remove' });
                    }
                  }}
                  className="h-6 text-xs gap-1 text-orange-600 hover:text-orange-700"
                  title="Remove Hardev, Other, and empty tags"
                >
                  <Trash2 className="h-3 w-3" /> Clean Tags
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setTagsDialog(true)}
                className="h-6 text-xs gap-1"
                data-testid="manage-tags-btn"
              >
                <Plus className="h-3 w-3" /> Add Tag
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openEditDialog} className="gap-2" data-testid="edit-client-btn">
            <Pencil className="h-4 w-4" />
            Edit Client
          </Button>
          {canDeleteClient && (
            <Button 
              variant="outline" 
              className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              onClick={handleDeleteClient}
              data-testid="delete-client-btn"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <CheckSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Open Tasks</p>
                <p className="text-2xl font-bold text-gray-900">{tasks.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Documents</p>
                <p className="text-2xl font-bold text-gray-900">{documents.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <DollarSign className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <User className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">Assigned To</p>
                <Select value={client.assignedTo || ''} onValueChange={handleAssignStaff}>
                  <SelectTrigger className="h-8 w-full mt-1">
                    <SelectValue placeholder="Select staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((member) => (
                      <SelectItem key={member._id} value={member.name}>{member.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger value="docs">Docs ({documents.length})</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="passwords">Passwords</TabsTrigger>
          <TabsTrigger value="workflow">Workflow</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <ClientOverviewTab 
            client={client}
            customFields={applicableCustomFields}
            linkedCorporations={linkedCorporations}
            ownerClient={ownerClient}
          />
        </TabsContent>

        <TabsContent value="tasks">
          <ClientTasksTab
            clientId={id}
            clientName={client.name}
            tasks={tasks}
            teamMembers={teamMembers}
            onRefresh={fetchTasks}
            toast={toast}
          />
        </TabsContent>

        <TabsContent value="docs">
          <ClientDocumentsTab
            clientId={id}
            clientName={client.name}
            documents={documents}
            canDeleteDocument={canDeleteDocument}
            onRefresh={fetchDocuments}
            toast={toast}
          />
        </TabsContent>

        <TabsContent value="invoices">
          <ClientInvoicesTab
            clientId={id}
            client={client}
            invoices={invoices}
            services={services}
            canDeleteInvoice={canDeleteInvoice}
            onRefresh={fetchInvoices}
            toast={toast}
          />
        </TabsContent>

        <TabsContent value="notes">
          <ClientNotesTab
            clientId={id}
            notes={notes}
            canDeleteNote={canDeleteNote}
            onRefresh={fetchNotes}
            toast={toast}
          />
        </TabsContent>

        <TabsContent value="passwords">
          <ClientPasswordsTab
            clientId={id}
            clientPasswords={clientPasswords}
            onRefresh={fetchClientPasswords}
            toast={toast}
          />
        </TabsContent>

        <TabsContent value="workflow">
          <ClientWorkflowTab
            clientPipelineAssignments={clientPipelineAssignments}
            pipelines={pipelines}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Client Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
          </DialogHeader>
          {editClient && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Type *</Label>
                  <Select value={editClient.type} onValueChange={(v) => setEditClient(prev => ({ ...prev, type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Individual">Individual</SelectItem>
                      <SelectItem value="Corporation">Corporation</SelectItem>
                      <SelectItem value="Others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <ValidatedInput
                    value={editClient.name}
                    onChange={(e) => setEditClient(prev => ({ ...prev, name: formatToTitleCase(e.target.value) }))}
                    validationType="name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Code</Label>
                  <Input
                    value={editClient.clientCode || ''}
                    onChange={(e) => setEditClient(prev => ({ ...prev, clientCode: e.target.value.toUpperCase() }))}
                    placeholder="e.g., PS1"
                    className="font-mono uppercase"
                    data-testid="edit-client-code-input"
                  />
                  <p className="text-xs text-gray-500">Auto-generated if left empty</p>
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <ValidatedInput type="email" value={editClient.email}
                    onChange={(e) => setEditClient(prev => ({ ...prev, email: formatEmail(e.target.value) }))}
                    validationType="email" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <ValidatedInput value={editClient.phone || ''}
                    onChange={(e) => setEditClient(prev => ({ ...prev, phone: formatPhoneNumber(e.target.value) }))}
                    validationType="phone" />
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-4">Address</h3>
                <AddressForm
                  address={editClient.address || { line1: '', line2: '', city: '', province: '', country: '', postalCode: '' }}
                  onChange={(addr) => setEditClient(prev => ({ ...prev, address: addr }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editClient.status} onValueChange={(v) => setEditClient(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Industry</Label>
                <Select value={editClient.industry || 'none'} onValueChange={(v) => setEditClient(prev => ({ ...prev, industry: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {industries.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Referral Source</Label>
                <Select value={editClient.referralSource || 'none'} onValueChange={(v) => setEditClient(prev => ({ ...prev, referralSource: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select referral source" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {referralSources.map((src) => (
                      <SelectItem key={src} value={src}>{src}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {editClient.type === 'Individual' && (
                <>
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-4">Individual Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date of Birth</Label>
                        <Input type="date" value={editClient.dateOfBirth || ''}
                          max={new Date().toISOString().split('T')[0]}
                          onChange={(e) => setEditClient(prev => ({ ...prev, dateOfBirth: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Marital Status</Label>
                        <Select value={editClient.maritalStatus || 'none'} 
                          onValueChange={(v) => setEditClient(prev => ({ ...prev, maritalStatus: v === 'none' ? '' : v }))}>
                          <SelectTrigger><SelectValue placeholder="Select marital status" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Not specified</SelectItem>
                            <SelectItem value="Single">Single</SelectItem>
                            <SelectItem value="Married">Married</SelectItem>
                            <SelectItem value="Common Law">Common Law</SelectItem>
                            <SelectItem value="Widowed">Widowed</SelectItem>
                            <SelectItem value="Divorced">Divorced</SelectItem>
                            <SelectItem value="Separated">Separated</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label>SIN</Label>
                        <ValidatedInput value={editClient.sin || ''}
                          onChange={(e) => setEditClient(prev => ({ ...prev, sin: formatSIN(e.target.value) }))}
                          validationType="sin" />
                      </div>
                      <div className="space-y-2">
                        <Label>GST/HST Number</Label>
                        <Input value={editClient.gstHstNumber || ''}
                          onChange={(e) => setEditClient(prev => ({ ...prev, gstHstNumber: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label>Payroll Number</Label>
                        <Input value={editClient.payrollNumber || ''}
                          onChange={(e) => setEditClient(prev => ({ ...prev, payrollNumber: e.target.value }))} />
                      </div>
                    </div>
                  </div>

                  {/* Spouse Information - Only show if marital status is NOT Single */}
                  {editClient.maritalStatus && editClient.maritalStatus !== 'Single' && (
                    <div className="border-t pt-4">
                      <h3 className="font-semibold mb-4">Spouse Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Spouse Name</Label>
                          <ValidatedInput value={editClient.spouse?.name || ''}
                            onChange={(e) => updateEditSpouse('name', formatToTitleCase(e.target.value))}
                            validationType="name" />
                        </div>
                        <div className="space-y-2">
                          <Label>Spouse Date of Birth</Label>
                          <Input type="date" value={editClient.spouse?.dateOfBirth || ''}
                            max={new Date().toISOString().split('T')[0]}
                            onChange={(e) => updateEditSpouse('dateOfBirth', e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Spouse SIN</Label>
                          <ValidatedInput value={editClient.spouse?.sin || ''}
                            onChange={(e) => updateEditSpouse('sin', formatSIN(e.target.value))}
                            validationType="sin" />
                        </div>
                        <div className="space-y-2">
                          <Label>Spouse Email</Label>
                          <ValidatedInput type="email" value={editClient.spouse?.email || ''}
                            onChange={(e) => updateEditSpouse('email', formatEmail(e.target.value))}
                            validationType="email" />
                        </div>
                        <div className="space-y-2">
                          <Label>Spouse Phone</Label>
                          <ValidatedInput value={editClient.spouse?.phone || ''}
                            onChange={(e) => updateEditSpouse('phone', formatPhoneNumber(e.target.value))}
                            validationType="phone" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Children Information - Only show if marital status is NOT Single */}
                  {editClient.maritalStatus && editClient.maritalStatus !== 'Single' && (
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold">Children Information</h3>
                        <Button type="button" variant="outline" size="sm" onClick={addEditChild} className="gap-1">
                          <Plus className="h-3 w-3" /> Add Child
                        </Button>
                      </div>
                      {(!editClient.children || editClient.children.length === 0) ? (
                        <p className="text-sm text-gray-500 text-center py-4">No children added. Click "Add Child" to add.</p>
                      ) : (
                        <div className="space-y-4">
                          {editClient.children.map((child, index) => (
                            <div key={index} className="p-4 border rounded-lg bg-gray-50">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-700">Child {index + 1}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeEditChild(index)}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Name</Label>
                                  <ValidatedInput
                                    placeholder="Child's name"
                                    value={child.name || ''}
                                    onChange={(e) => updateEditChild(index, 'name', formatToTitleCase(e.target.value))}
                                    validationType="name"
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Date of Birth</Label>
                                  <Input
                                    type="date"
                                    value={child.dateOfBirth || ''}
                                    max={new Date().toISOString().split('T')[0]}
                                    onChange={(e) => updateEditChild(index, 'dateOfBirth', e.target.value)}
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">SIN</Label>
                                  <ValidatedInput
                                    placeholder="123-456-789"
                                    value={child.sin || ''}
                                    onChange={(e) => updateEditChild(index, 'sin', formatSIN(e.target.value))}
                                    validationType="sin"
                                    className="h-9"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {editClient.type === 'Corporation' && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">Corporation Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Business Number (BN)</Label>
                      <Input value={editClient.businessNumber || ''}
                        onChange={(e) => setEditClient(prev => ({ ...prev, businessNumber: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Year End</Label>
                      <Select value={editClient.yearEnd || ''} onValueChange={(v) => setEditClient(prev => ({ ...prev, yearEnd: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select year end" /></SelectTrigger>
                        <SelectContent>
                          {['January 31', 'February 28', 'March 31', 'April 30', 'May 31', 'June 30',
                            'July 31', 'August 31', 'September 30', 'October 31', 'November 30', 'December 31'].map(d => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>GST/HST Number</Label>
                      <Input value={editClient.gstHstNumber || ''}
                        onChange={(e) => setEditClient(prev => ({ ...prev, gstHstNumber: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Payroll Number</Label>
                      <Input value={editClient.payrollNumber || ''}
                        onChange={(e) => setEditClient(prev => ({ ...prev, payrollNumber: e.target.value }))} />
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <Label>Owner (Individual Client)</Label>
                    <ClientSearchInput
                      value={editClient.ownerId}
                      onSelect={(id, name) => setEditClient(prev => ({ ...prev, ownerId: id }))}
                      placeholder="Search individual owner..."
                      typeFilter="Individual"
                      showPhone={true}
                      allowClear={true}
                      data-testid="edit-owner-search-input"
                    />
                  </div>
                </div>
              )}

              {/* Custom Fields Section */}
              {getApplicableCustomFields().length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">Custom Fields</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {getApplicableCustomFields().map((field) => (
                      <div key={field._id} className="space-y-2">
                        <Label>
                          {field.name}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        {(field.type === 'conditional_number' || field.type === 'conditional_dropdown') ? (
                          <ConditionalField
                            field={field}
                            value={editClient.customFields?.[field.fieldId]}
                            onChange={(v) => updateEditCustomField(field.fieldId, v)}
                          />
                        ) : field.type === 'select' || field.type === 'dropdown' ? (
                          <Select 
                            value={editClient.customFields?.[field.fieldId] || ''} 
                            onValueChange={(v) => updateEditCustomField(field.fieldId, v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={`Select ${field.name}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {(field.options || []).map((option) => (
                                <SelectItem key={option.id || option} value={option.label || option}>
                                  {option.label || option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : field.type === 'date' ? (
                          <Input 
                            type="date"
                            value={editClient.customFields?.[field.fieldId] || ''}
                            onChange={(e) => updateEditCustomField(field.fieldId, e.target.value)}
                          />
                        ) : field.type === 'number' ? (
                          <Input 
                            type="number"
                            value={editClient.customFields?.[field.fieldId] || ''}
                            onChange={(e) => updateEditCustomField(field.fieldId, e.target.value)}
                          />
                        ) : (
                          <Input 
                            value={editClient.customFields?.[field.fieldId] || ''}
                            onChange={(e) => updateEditCustomField(field.fieldId, e.target.value)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
                <Button onClick={handleUpdateClient}>Save Changes</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tags Management Dialog */}
      <Dialog open={tagsDialog} onOpenChange={setTagsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Tags</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex flex-wrap gap-2">
              {(client?.tags || []).map((tag) => (
                <Badge key={tag} variant="default" className="gap-1">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="ml-1 hover:text-red-300">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="space-y-2">
              <Label>Add Tag</Label>
              <div className="flex flex-wrap gap-2">
                {allTags.filter(t => !client?.tags?.includes(t.name)).map((tag) => (
                  <Button
                    key={tag._id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddTag(tag.name)}
                    className="h-7 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> {tag.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTagsDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task for {client?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="Task title"
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Task description..."
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assign To *</Label>
                <Select value={newTask.assignedTo} onValueChange={(v) => setNewTask(prev => ({ ...prev, assignedTo: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((member) => (
                      <SelectItem key={member._id} value={member.name}>{member.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={newTask.status} onValueChange={(v) => setNewTask(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note for {client?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2 relative">
              <Label>Note Content * <span className="text-xs text-gray-500">(Type @ to mention team members)</span></Label>
              <Textarea
                placeholder="Enter your note... Type @ to mention someone"
                rows={5}
                value={newNote.content}
                onChange={handleNoteContentChange}
                data-testid="note-content-input"
              />
              {/* @ Mention Suggestions */}
              {showMentionSuggestions && filteredMentionMembers.length > 0 && (
                <div className="absolute bottom-full left-0 w-full bg-white border rounded-lg shadow-lg mb-1 max-h-40 overflow-y-auto z-50">
                  {filteredMentionMembers.map((member) => (
                    <div
                      key={member._id}
                      className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex items-center gap-2"
                      onClick={() => handleSelectMention(member)}
                    >
                      <div className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs">
                        {(member.firstName || member.name || 'U').charAt(0)}
                      </div>
                      <span className="text-sm">
                        {member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : member.name}
                      </span>
                      <span className="text-xs text-gray-500">({member.role})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="pinNote"
                checked={newNote.isPinned}
                onChange={(e) => setNewNote(prev => ({ ...prev, isPinned: e.target.checked }))}
              />
              <Label htmlFor="pinNote">Pin this note</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateNote}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Invoice Dialog - Using InvoiceForm Component */}
      <InvoiceForm
        open={invoiceDialog}
        onOpenChange={setInvoiceDialog}
        preselectedClientId={id}
        preselectedClientName={client?.name}
        onSuccess={fetchInvoices}
      />

      {/* Edit Invoice Dialog */}
      <Dialog open={editInvoiceDialog} onOpenChange={setEditInvoiceDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Invoice {editInvoice?.invoiceNumber}</DialogTitle>
          </DialogHeader>
          {editInvoice && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={editInvoice.dueDate ? new Date(editInvoice.dueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditInvoice(prev => ({ ...prev, dueDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>GST/HST Rate (%)</Label>
                  <Input
                    type="number"
                    value={editInvoice.hstRate || 13}
                    onChange={(e) => setEditInvoice(prev => ({ ...prev, hstRate: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Line Items</Label>
                  <Button variant="outline" size="sm" onClick={addEditInvoiceLineItem} className="gap-1">
                    <Plus className="h-4 w-4" /> Add Item
                  </Button>
                </div>
                
                {(editInvoice.lineItems || []).map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Service</Label>
                      <Input
                        value={item.service || ''}
                        onChange={(e) => updateEditInvoiceLineItem(index, 'service', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Year</Label>
                      <Input
                        value={item.year || ''}
                        onChange={(e) => updateEditInvoiceLineItem(index, 'year', e.target.value)}
                      />
                    </div>
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={item.description || ''}
                        onChange={(e) => updateEditInvoiceLineItem(index, 'description', e.target.value)}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Amount</Label>
                      <Input
                        type="number"
                        value={item.amount || ''}
                        onChange={(e) => updateEditInvoiceLineItem(index, 'amount', e.target.value)}
                      />
                    </div>
                    <div className="col-span-1">
                      {(editInvoice.lineItems || []).length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeEditInvoiceLineItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-48 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>${calculateEditInvoiceTotals().subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>GST/HST ({editInvoice.hstRate || 13}%):</span>
                      <span>${calculateEditInvoiceTotals().hstAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-1">
                      <span>Total:</span>
                      <span>${calculateEditInvoiceTotals().grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditInvoiceDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateInvoice}>Update Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Invoice Dialog */}
      <Dialog open={deleteInvoiceDialog} onOpenChange={setDeleteInvoiceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Invoice</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete invoice <strong>{invoiceToDelete?.invoiceNumber}</strong>?
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteInvoiceDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteInvoice}>Delete Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <InvoiceViewDialog
        open={viewInvoiceDialog}
        onOpenChange={setViewInvoiceDialog}
        invoice={viewingInvoice}
      />

      {/* Payment Mode Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-gray-600">
              Select the payment mode for invoice <strong>{invoiceToPay?.invoiceNumber}</strong>
            </p>
            <div className="space-y-2">
              <Label>Payment Mode *</Label>
              <Select value={selectedPaymentMode} onValueChange={setSelectedPaymentMode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="etransfer">E-Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmPayment}>Mark as Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Message Dialog */}
      <Dialog open={messageDialog} onOpenChange={setMessageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Message to {client?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                placeholder="Message subject"
                value={newMessage.subject}
                onChange={(e) => setNewMessage(prev => ({ ...prev, subject: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                placeholder="Type your message..."
                rows={5}
                value={newMessage.body}
                onChange={(e) => setNewMessage(prev => ({ ...prev, body: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageDialog(false)}>Cancel</Button>
            <Button onClick={handleSendMessage} className="gap-2">
              <Send className="h-4 w-4" /> Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={documentDialog} onOpenChange={setDocumentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document for {client?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Name *</Label>
              <Input
                placeholder="e.g., 2024 Tax Return"
                value={newDocument.name}
                onChange={(e) => setNewDocument(prev => ({ ...prev, name: e.target.value }))}
                data-testid="document-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={newDocument.type} onValueChange={(v) => setNewDocument(prev => ({ ...prev, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Tax Return">Tax Return</SelectItem>
                  <SelectItem value="Financial Statement">Financial Statement</SelectItem>
                  <SelectItem value="Receipt">Receipt</SelectItem>
                  <SelectItem value="Contract">Contract</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Choose File</Label>
              <Input
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setNewDocument(prev => ({
                      ...prev,
                      file,
                      name: prev.name || file.name
                    }));
                  }
                }}
                className="cursor-pointer"
                data-testid="document-file-input"
              />
              {newDocument.file && (
                <p className="text-sm text-gray-500">
                  Selected: {newDocument.file.name} ({(newDocument.file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={uploadingDoc} data-testid="upload-document-submit">
              {uploadingDoc ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={viewDocDialog} onOpenChange={setViewDocDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Document Details</DialogTitle>
          </DialogHeader>
          {viewingDoc && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <FileText className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{viewingDoc.name}</h3>
                  <p className="text-sm text-gray-500">{viewingDoc.type}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 py-4 border-y">
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-medium">{viewingDoc.clientName || client?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={getDocStatusColor(viewingDoc.status)}>
                    {(viewingDoc.status || 'pending').replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Size</p>
                  <p className="font-medium">{viewingDoc.size || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Uploaded By</p>
                  <p className="font-medium">{viewingDoc.uploadedBy || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Upload Date</p>
                  <p className="font-medium">
                    {viewingDoc.createdAt ? format(new Date(viewingDoc.createdAt), 'MMM dd, yyyy h:mm a') : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">File</p>
                  <p className="font-medium">
                    {viewingDoc.fileUrl ? (
                      <Badge variant="outline" className="text-green-600">Attached</Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-500">No file</Badge>
                    )}
                  </p>
                </div>
              </div>

              {viewingDoc.fileUrl && (
                <div className="flex gap-2">
                  <Button 
                    className="flex-1 gap-2"
                    onClick={() => handleDownloadDocument(viewingDoc)}
                  >
                    <Download className="h-4 w-4" />
                    Download File
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${viewingDoc.fileUrl}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open in New Tab
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDocDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Add Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Service Name *</Label>
              <Input
                placeholder="e.g., CRA Account, QuickBooks"
                value={newPassword.serviceName}
                onChange={(e) => setNewPassword(prev => ({ ...prev, serviceName: e.target.value }))}
                data-testid="password-service-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                placeholder="https://..."
                value={newPassword.website}
                onChange={(e) => setNewPassword(prev => ({ ...prev, website: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                placeholder="Username or email"
                value={newPassword.username}
                onChange={(e) => setNewPassword(prev => ({ ...prev, username: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                placeholder="Enter password"
                value={newPassword.password}
                onChange={(e) => setNewPassword(prev => ({ ...prev, password: e.target.value }))}
                data-testid="password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Additional notes..."
                value={newPassword.notes}
                onChange={(e) => setNewPassword(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            
            {/* Custom Fields */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Custom Fields</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewPassword(prev => ({
                    ...prev,
                    customFields: [...(prev.customFields || []), { fieldName: '', fieldValue: '' }]
                  }))}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Field
                </Button>
              </div>
              {(newPassword.customFields || []).map((field, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    placeholder="Field name"
                    value={field.fieldName}
                    onChange={(e) => {
                      const updated = [...newPassword.customFields];
                      updated[idx].fieldName = e.target.value;
                      setNewPassword(prev => ({ ...prev, customFields: updated }));
                    }}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Value"
                    type="password"
                    value={field.fieldValue}
                    onChange={(e) => {
                      const updated = [...newPassword.customFields];
                      updated[idx].fieldValue = e.target.value;
                      setNewPassword(prev => ({ ...prev, customFields: updated }));
                    }}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500"
                    onClick={() => {
                      const updated = newPassword.customFields.filter((_, i) => i !== idx);
                      setNewPassword(prev => ({ ...prev, customFields: updated }));
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPassword} data-testid="save-password-btn">Save Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Password Dialog */}
      <Dialog open={editPasswordDialog} onOpenChange={setEditPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Edit Password
            </DialogTitle>
          </DialogHeader>
          {editingPassword && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  value={editingPassword.serviceName}
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, serviceName: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={editingPassword.website || ''}
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, website: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={editingPassword.username || ''}
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, username: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="text"
                  value={editingPassword.password}
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, password: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editingPassword.notes || ''}
                  onChange={(e) => setEditingPassword(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdatePassword}>Update Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;
